import { useCallback } from 'react';
import { Appearance, Linking, Platform } from 'react-native';
import { getLinkToBank, getRenewAuth, getSyncingStatus, linkPlaidConnection, setConnectionsStatus, } from 'actions';
import { StatusBar } from 'design-system';
import { useAppDispatch, useAppStore } from 'store/hooks';
import { openPlaidRenew } from 'utils/plaidlink';
import Sentry from 'utils/sentry';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectConnectionById, selectIsLoggedIn } from 'reducers/selectors';
import useRenewedConnection from 'features/link-bank/hooks/useRenewedConnection';
import Modal from 'utils/packages/Modal';
import useLinkedConnection from 'features/link-bank/hooks/useLinkedConnection';
import InAppBrowser from '../utils/packages/inAppBrowser';
const options = {
    animated: true,
    modalTransitionStyle: 'coverVertical',
    modalEnabled: false,
    forceCloseOnRedirection: Platform.OS === 'android',
};
const useLinkConnection = () => {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const store = useAppStore();
    const { handleRenewedConnection } = useRenewedConnection();
    const { handleLinkedConnection } = useLinkedConnection();
    const link = useCallback(async (bankInfo, id, keyBack, isRenewing) => {
        const { loginType, type } = bankInfo;
        const isLoggedIn = selectIsLoggedIn(store.getState());
        if (loginType === 'OAUTH2') {
            const response = await dispatch(isRenewing
                ? getRenewAuth(id, isLoggedIn, selectConnectionById(store.getState(), id))
                : getLinkToBank(id, isLoggedIn));
            if (response) {
                if (response.type !== 'LINK_SUCCESS' || !response.payload)
                    return null;
                const cb = async () => {
                    const { authLink, appLink } = response.payload;
                    if (appLink) {
                        Linking.openURL(appLink).catch(async (err) => {
                            Sentry.logError({
                                filename: 'banks.js',
                                message: `Open applink failed, link: ${appLink}`,
                                err,
                            });
                            // Attempt to open the auth link in a safari view, since Linking.openUrl failed to open appLink
                            if (Platform.OS === 'ios' && authLink) {
                                if (await InAppBrowser.isAvailable()) {
                                    const style = StatusBar.pushStackEntryWithoutChecking({
                                        barStyle: Appearance.getColorScheme() === 'dark'
                                            ? 'light-content'
                                            : 'dark-content',
                                    });
                                    InAppBrowser.open(authLink, options)
                                        .catch(() => {
                                        Sentry.logError({
                                            filename: 'banks.js',
                                            message: `Opening fallback authLink failed, link: ${authLink}`,
                                            err,
                                        });
                                    })
                                        .finally(() => {
                                        StatusBar.popStackEntry(style);
                                    });
                                }
                            }
                            else {
                                Modal.showConfirmation({
                                    title: 'App not found',
                                    subtitle: `To connect your ${bankInfo.name} account, you need the ${bankInfo.name} app installed on your device. Please download it and try again.`,
                                    confirmButtonTitle: 'Okay',
                                });
                            }
                        });
                    }
                    else if (authLink && (await InAppBrowser.isAvailable())) {
                        const style = StatusBar.pushStackEntryWithoutChecking({
                            barStyle: Appearance.getColorScheme() === 'dark'
                                ? 'light-content'
                                : 'dark-content',
                        });
                        InAppBrowser.open(authLink, options)
                            .catch(() => { })
                            .finally(() => {
                            StatusBar.popStackEntry(style);
                        });
                    }
                };
                if (Modal.isAnyModalOpen()) {
                    Modal.hide(cb);
                }
                else {
                    cb();
                }
                return response;
            }
            return null;
        }
        if (loginType === 'APIKEY') {
            if (type === 'CRYPTO_EXCHANGE')
                navigation.navigate('CryptoLogin', {
                    data: bankInfo,
                    bankConnectionId: isRenewing ? id : undefined,
                    keyBack,
                });
        }
        else if (type === 'CRYPTO_WALLET') {
            navigation.navigate('CryptoAddressLogin', {
                data: bankInfo,
                keyBack,
            });
        }
        else if (bankInfo.provider === 'PLAID' && isRenewing) {
            const connection = selectConnectionById(store.getState(), id);
            const response = await dispatch(getRenewAuth(id, isLoggedIn, connection));
            if (response?.payload?.authLink) {
                const cb = () => openPlaidRenew(response, (data) => dispatch(linkPlaidConnection(data)), (isSuccess) => {
                    const state = store.getState();
                    const { renewingConnection, linkingBankId } = state.linkBank;
                    if (renewingConnection && isLoggedIn && isSuccess) {
                        handleRenewedConnection(renewingConnection);
                    }
                    else if (linkingBankId && isLoggedIn && isSuccess) {
                        handleLinkedConnection(linkingBankId);
                    }
                    else {
                        dispatch(setConnectionsStatus(true));
                        dispatch(getSyncingStatus());
                    }
                });
                if (Modal.isAnyModalOpen()) {
                    Modal.hide(cb);
                }
                else {
                    cb();
                }
                return response;
            }
        }
        return null;
    }, [handleLinkedConnection, handleRenewedConnection]);
    const linkConnection = useCallback(async (bankInfo, keyBack = null) => link(bankInfo, bankInfo.id, keyBack), [link]);
    const renewConnection = useCallback(async (bankInfo, connectionId, keyBack = null) => link(bankInfo, connectionId, keyBack, true), [link]);
    return { linkConnection, renewConnection };
};
export default useLinkConnection;
